/*
 * Portions of this component's visual styling are adapted from shadcn/ui.
 * Copyright (c) 2023 shadcn. Licensed under the MIT License.
 */

import { IconChevronDown } from "./icons/chevron-down.js";

interface AccordionItem {
  value: string;
  title: string;
  content: string;
}

const styles = css`
  :host {
    display: block;
  }

  .item {
    border-bottom: 1px solid var(--border, #e4e4e7);
  }

  .trigger {
    align-items: center;
    background: transparent;
    border: 0;
    color: var(--foreground, #18181b);
    cursor: pointer;
    display: flex;
    font: inherit;
    font-size: 0.9rem;
    font-weight: 500;
    gap: 12px;
    justify-content: space-between;
    outline: none;
    padding: 14px 2px;
    text-align: left;
    width: 100%;
  }

  .trigger:hover {
    text-decoration: underline;
  }

  .trigger:focus-visible {
    border-radius: 6px;
    box-shadow: 0 0 0 3px var(--ring-shadow, rgb(24 24 27 / 12%));
  }

  .chevron {
    color: var(--muted-foreground, #71717a);
    flex: 0 0 auto;
    transition: transform 180ms ease;
  }

  .trigger[aria-expanded="true"] .chevron {
    transform: rotate(180deg);
  }

  .content {
    color: var(--muted-foreground, #71717a);
    font-size: 0.875rem;
    line-height: 1.5;
    padding: 0 2px 14px;
  }
`;

export class AppAccordion extends HTMLElement {
  static argonHydrate(element) {
    if (!(element instanceof AppAccordion)) return;
    if (element.isConnected && !element.#bindingsMounted) {
      element.#syncHydratedProps();
      element.connectedCallback();
    }
    if (element.#root) element.#wireEvents(element.#root);
  }
  static observedAttributes = ["items","data-items","value","data-value","type","data-type"];

  #items = [];
  #value = [];
  #type = "single";
  #itemsSet = false;
  #valueSet = false;
  #typeSet = false;
  #textStart0;
  #textEnd0;
  #lists = {};
  #root;
  #bindingsMounted = false;
  #hydrating = false;
  #isCSR = false;

  get items() {
    return this.#items;
  }

  set items(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#items, next)) return;
    this.#items = next;
    this.#itemsSet = true;
    this.#updateBindings('items');
  }

  get value() {
    return this.#value;
  }

  set value(value) {
    const raw = value ?? [];
    const next = raw == null ? raw : typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (Object.is(this.#value, next)) return;
    this.#value = next;
    this.#valueSet = true;
    this.#updateBindings('value');
  }

  get type() {
    return this.#type;
  }

  set type(value) {
    const raw = value ?? "single";
    const next = raw == null ? raw : raw;
    if (Object.is(this.#type, next)) return;
    this.#type = next;
    this.#typeSet = true;
    this.#updateBindings('type');
  }

  #syncHydratedProps() {
    if (this.#isCSR) return;
    if (this.hasAttribute('data-items')) this.items = this.dataset.items;
    if (this.hasAttribute('data-value')) this.value = this.dataset.value;
    if (this.hasAttribute('data-type')) this.type = this.dataset.type;
  }

  constructor(props) {
    super();
    if (props !== undefined) {
      this.#isCSR = true;
      this.items = props.items;
      this.value = props.value;
      this.type = props.type;
      this.#initState();
      const shadow = this.attachShadow({ mode: 'open' });
      shadow.innerHTML = this.#render();
      this.#mount(shadow, false);
    }
  }

  connectedCallback() {
    if (this.#isCSR || this.#bindingsMounted) return;
    this.#syncHydratedProps();
    if (!this.#itemsSet) this.items = this.dataset.items;
    if (!this.#valueSet) this.value = this.dataset.value;
    if (!this.#typeSet) this.type = this.dataset.type;
    this.#initState();
    const shadow = this.shadowRoot;
    const root = shadow ?? this.attachShadow({ mode: 'open' });
    if (!shadow) root.innerHTML = this.#render();
    this.#mount(root, !!shadow);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    switch (name) {
      case 'items':
      case 'data-items':
        this.items = newValue;
        break;
      case 'value':
      case 'data-value':
        this.value = newValue;
        break;
      case 'type':
      case 'data-type':
        this.type = newValue;
        break;
    }
  }

  #initState() {

  }

  #render() {
    
    return `<style>${this.#escText(styles)}</style><div class="root" data-argon="1611"><!--argon-text:0-->${this.#items.map((item) => { const expanded = this.#value.includes(item.value); return `<div class="item" data-value="${this.#escAttr(item.value)}"><button class="trigger" type="button" data-value="${this.#escAttr(item.value)}" aria-expanded="${this.#escAttr(expanded ? "true" : "false")}"><span>${this.#escText(item.title)}</span><span class="chevron" aria-hidden="true"><icon-chevron-down>${""}</icon-chevron-down></span></button>${expanded ? `<div class="content">${this.#escText(item.content)}</div>` : this.#escText("")}</div>`; }).join("")}<!--/argon-text:0--></div>`;
  }



  #mount(root, hydrating) {
    this.#root = root;
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_COMMENT);
    let marker;
    while ((marker = walker.nextNode())) {
      switch (marker.nodeValue) {
        case 'argon-text:0':
          this.#textStart0 = marker;
          break;
        case '/argon-text:0':
          this.#textEnd0 = marker;
          break;
      }
    }
    this.#bindingsMounted = true;
    this.#hydrating = hydrating;
    this.#updateBindings();
    this.#hydrating = false;
    this.#wireEvents(root);
    if (!root.__argonEventObserver) {
      const observer = new window.MutationObserver(() => this.#wireEvents(root));
      observer.observe(root, { childList: true, subtree: true });
      Object.defineProperty(root, '__argonEventObserver', { value: observer });
    }
    this.setAttribute('hydrated', '');
    setTimeout(() => { if (this.#root === root) this.#wireEvents(root); }, 0);
  }

  #replaceRange(start, end, value) {
    if (!start || !end) return;
    const html = String(value);
    const sole = start.nextSibling;
    if (sole && sole !== end && sole.nextSibling === end && sole.nodeType === 3 && !/[<&]/.test(html)) {
      if (html.startsWith(sole.data)) sole.appendData(html.slice(sole.data.length));
      else sole.data = html;
      return;
    }
    const template = document.createElement('template');
    template.innerHTML = html;
    if (sole && sole !== end && sole.nextSibling === end && template.content.childNodes.length === 1 && this.#patchElement(sole, template.content.firstChild)) return;
    let oldNode = start.nextSibling;
    let newNode = template.content.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode || oldNode === end) {
        end.before(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldNode.before(newNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode && oldNode !== end) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #patchElement(oldEl, newEl) {
    if (!oldEl || !newEl || oldEl.nodeType !== 1 || newEl.nodeType !== 1 || oldEl.localName !== newEl.localName) return false;
    for (const attr of [...oldEl.attributes]) {
      if (attr.name !== 'hydrated' && !newEl.hasAttribute(attr.name)) oldEl.removeAttribute(attr.name);
    }
    for (const attr of [...newEl.attributes]) {
      if (oldEl.getAttribute(attr.name) !== attr.value) oldEl.setAttribute(attr.name, attr.value);
    }
    this.#patchChildren(oldEl, newEl);
    this.#wireEvents(oldEl);
    return true;
  }

  #patchNode(oldNode, newNode) {
    if (!oldNode || !newNode) return false;
    if (oldNode.nodeType === 3 && newNode.nodeType === 3) {
      if (oldNode.data !== newNode.data) oldNode.data = newNode.data;
      return true;
    }
    if (oldNode.nodeType === 8 && newNode.nodeType === 8) {
      if (oldNode.nodeValue !== newNode.nodeValue) oldNode.nodeValue = newNode.nodeValue;
      return true;
    }
    return this.#patchElement(oldNode, newNode);
  }

  #patchChildren(oldEl, newEl) {
    let oldNode = oldEl.firstChild;
    let newNode = newEl.firstChild;
    while (newNode) {
      const nextNew = newNode.nextSibling;
      if (!oldNode) {
        oldEl.append(newNode);
        if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
      } else {
        const nextOld = oldNode.nextSibling;
        if (!this.#patchNode(oldNode, newNode)) {
          oldEl.insertBefore(newNode, oldNode);
          oldNode.remove();
          if (newNode.nodeType === 1 || newNode.nodeType === 11) this.#wireEvents(newNode);
        }
        oldNode = nextOld;
      }
      newNode = nextNew;
    }
    while (oldNode) {
      const nextOld = oldNode.nextSibling;
      oldNode.remove();
      oldNode = nextOld;
    }
  }

  #adoptList(start, end, htmls) {
    const specs = [];
    let node = start.nextSibling;
    for (const html of htmls) {
      const template = document.createElement('template');
      template.innerHTML = html;
      const nodes = [];
      const anchor = node;
      for (const fresh of template.content.childNodes) {
        if (!node || node === end || node.nodeType !== fresh.nodeType || (node.nodeType === 1 && node.localName !== fresh.localName)) return;
        nodes.push(node);
        node = node.nextSibling;
      }
      specs.push({ anchor, nodes });
    }
    if (node !== end) return;
    return specs.map(({ anchor, nodes }) => {
      const mark = document.createComment('argon-item');
      (anchor ?? end).before(mark);
      return [mark, ...nodes];
    });
  }

  #reconcileList(id, start, end, items) {
    if (!start || !end) return;
    let list = this.#lists[id];
    if (!list) {
      const htmls = items.map(String);
      const nodes = this.#hydrating ? this.#adoptList(start, end, htmls) : undefined;
      if (nodes) list = this.#lists[id] = { htmls, marks: nodes.map(nodes => nodes[0]) };
      else {
        let node = start.nextSibling;
        while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
        list = this.#lists[id] = { htmls: [], marks: [] };
      }
    }
    const { htmls, marks } = list;
    if (items.length < htmls.length) {
      let node = marks[items.length];
      while (node && node !== end) { const next = node.nextSibling; node.remove(); node = next; }
      htmls.length = items.length;
      marks.length = items.length;
    }
    for (let i = 0; i < items.length; i++) {
      const html = String(items[i]);
      if (i < htmls.length) {
        const stop = i + 1 < marks.length ? marks[i + 1] : end;
        if (htmls[i] === html) {
          continue;
        }
        let node = marks[i].nextSibling;
        while (node && node !== stop) { const next = node.nextSibling; node.remove(); node = next; }
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        stop.before(template.content);
        htmls[i] = html;
      } else {
        const mark = document.createComment('argon-item');
        const template = document.createElement('template');
        template.innerHTML = html;
        this.#wireEvents(template.content);
        end.before(mark, template.content);
        marks.push(mark);
        htmls.push(html);
      }
    }
  }
  #escText(v) {
    return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  #escAttr(v) {
    return String(v).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
  }


  #eventTargets(scope, id) {
    const selector = `[data-argon="${id}"]`;
    const targets = [...scope.querySelectorAll(selector)];
    if (scope.nodeType === 1 && scope.matches(selector)) targets.unshift(scope);
    return targets;
  }

  #listen(el, id, event, handler) {
    const key = id + ':' + event;
    const root = this.#root;
    if (!root.__argonListeners) Object.defineProperty(root, '__argonListeners', { value: {} });
    const prev = root.__argonListeners[key];
    if (prev) root.removeEventListener(event, prev, true);
    const next = event => {
      const target = event.composedPath().find(node => node?.getAttribute?.('data-argon') === id);
      if (!target) return;
      const delegated = new Proxy(event, { get(source, prop) {
        if (prop === 'currentTarget') return target;
        const value = Reflect.get(source, prop, source);
        return typeof value === 'function' ? value.bind(source) : value;
      } });
      handler.call(target, delegated);
    };
    root.__argonListeners[key] = next;
    root.addEventListener(event, next, true);
  }

  #updateBindings(prop) {
    if (!this.#bindingsMounted) return;
    
    if (prop === undefined || prop === 'items' || prop === 'value') {
      this.#reconcileList(0, this.#textStart0, this.#textEnd0, this.#items.map((item) => { const expanded = this.#value.includes(item.value); return `<div class="item" data-value="${this.#escAttr(item.value)}"><button class="trigger" type="button" data-value="${this.#escAttr(item.value)}" aria-expanded="${this.#escAttr(expanded ? "true" : "false")}"><span>${this.#escText(item.title)}</span><span class="chevron" aria-hidden="true"><icon-chevron-down>${""}</icon-chevron-down></span></button>${expanded ? `<div class="content">${this.#escText(item.content)}</div>` : this.#escText("")}</div>`; }));
    }
  }

  #wireEvents(scope) {
    const root = this.#root;
    this.#eventTargets(scope, '1611').forEach(el => this.#listen(el, '1611', 'click', (event: Event) => {
    const trigger = (event.target as Element).closest(".trigger");
    if (!trigger)
        return;
    const item = trigger.getAttribute("data-value") ?? "";
    const next = this.#value.includes(item)
        ? this.#value.filter((entry) => entry !== item)
        : this.#type === "multiple"
            ? [...this.#value, item]
            : [item];
    this.dispatchEvent(new CustomEvent("value-change", { bubbles: true, composed: true, detail: { value: next } }));
}));
    const roots = [scope];
    for (let index = 0; index < roots.length; index++) {
      const candidates = roots[index].nodeType === 1 ? [roots[index], ...roots[index].querySelectorAll('*')] : [...roots[index].querySelectorAll('*')];
      for (const element of candidates) {
        customElements.upgrade(element);
        const hydrate = () => {
          customElements.upgrade(element);
          const definition = customElements.get(element.localName);
          definition?.argonHydrate?.(element);
        };
        if (element.isConnected && customElements.get(element.localName)) hydrate();
        else if (element.localName.includes('-')) customElements.whenDefined(element.localName).then(hydrate);
        else queueMicrotask(hydrate);
        if (element.shadowRoot) roots.push(element.shadowRoot);
      }
    }
  }
}
